import { Platform } from 'react-native'
import * as Updates from 'expo-updates'
import { otaClient } from '../updates/ota-client'

/**
 * A rollback is the only OTA operation allowed to run before the app shell is
 * loaded. The production incident proved that waiting for the full root tree
 * is unsafe: a bad Fabric-era bundle can crash while that tree is importing,
 * so the hook that would have handled the rollback never mounts.
 */
const EARLY_RECOVERY_TIMEOUT_MS = 2200

let recoveryInFlight: Promise<boolean> | null = null
let rollbackReloadInFlight = false

type RollbackResult = {
  isRollBackToEmbedded?: boolean
}

function reportBreadcrumb(message: string, data?: Record<string, unknown>): void {
  // Observability is deliberately best-effort here. This module runs before
  // the application graph and must never make startup depend on Sentry.
  void import('./sentry')
    .then(({ addBreadcrumb }) => {
      addBreadcrumb({ category: 'ota', level: 'info', message, data })
    })
    .catch(() => {})
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), timeoutMs)
    void promise
      .then((value) => {
        clearTimeout(timer)
        resolve(value)
      })
      .catch(() => {
        clearTimeout(timer)
        resolve(null)
      })
  })
}

function isSupportedNativeLaunch(): boolean {
  return Platform.OS !== 'web' && !__DEV__ && Updates.isEnabled && !Updates.isEmergencyLaunch
}

/**
 * Fetch and apply an Expo rollback directive. Returns true when a reload was
 * requested, false when there is no rollback or the network did not answer in
 * time. A normal OTA is never fetched here.
 */
export async function applyEmbeddedRollback(
  reason: string,
  timeoutMs = EARLY_RECOVERY_TIMEOUT_MS,
): Promise<boolean> {
  if (!isSupportedNativeLaunch()) return false
  if (Updates.isEmbeddedLaunch && Updates.isUsingEmbeddedAssets) return false
  if (rollbackReloadInFlight) return true

  const fetched = await withTimeout(otaClient.fetch(), timeoutMs)
  if (!fetched?.isRollBackToEmbedded) return false

  rollbackReloadInFlight = true
  reportBreadcrumb('Applying emergency rollback to embedded assets', {
    reason,
    updateId: Updates.updateId ?? 'unknown',
    runtimeVersion: Updates.runtimeVersion ?? 'unknown',
  })

  try {
    await Updates.reloadAsync()
    return true
  } catch {
    rollbackReloadInFlight = false
    reportBreadcrumb('Emergency rollback reload failed', { reason })
    return false
  }
}

/**
 * Check for a rollback before importing RootLayoutApp. It is intentionally
 * single-flight so the entry module and the mounted OTA hook cannot download
 * the same recovery directive concurrently.
 */
export function recoverEmbeddedRollback(reason = 'root-entry'): Promise<boolean> {
  if (!isSupportedNativeLaunch()) return Promise.resolve(false)
  if (recoveryInFlight) return recoveryInFlight

  recoveryInFlight = (async () => {
    const startedAt = Date.now()
    const result = await withTimeout(otaClient.check(), EARLY_RECOVERY_TIMEOUT_MS)
    if (!(result as RollbackResult | null)?.isRollBackToEmbedded) return false
    const remainingMs = EARLY_RECOVERY_TIMEOUT_MS - (Date.now() - startedAt)
    if (remainingMs <= 0) return false
    return applyEmbeddedRollback(reason, remainingMs)
  })()
    .catch(() => false)
    .finally(() => {
      recoveryInFlight = null
    })

  return recoveryInFlight
}
